import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
	Button,
	Col,
	DatePicker,
	Flex,
	Form,
	notification,
	Row,
	Select,
	Table,
	Tag,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faFilter } from "@fortawesome/pro-regular-svg-icons";
import dayjs from "dayjs";

import { GET } from "../../../providers/useAxiosQuery";
import { system_id, userData } from "../../../providers/companyInfo";
import { TableGlobalSearchAnimated } from "../../../providers/CustomTableFilter";
import ModalPreviewPdf from "../../../providers/ModalPreviewPdf";

export default function PageReports() {
	const location = useLocation();
	const [form] = Form.useForm();

	const [toggleModalPreviewPdf, setToggleModalPreviewPdf] = useState({
		open: false,
		url: null,
	});
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);

	const [tableFilter, setTableFilter] = useState({
		search: "",
		sort_field: "fullname",
		sort_order: "asc",
		form_id: "",
		department_ids: "",
		date_range: "",
		rate_status: "",
		from: location.pathname,
	});

	const { data: dataDepartment } = GET(
		`api/department`,
		"department_dropdown",
		(res) => {},
		false,
	);

	const { data: dataForm } = GET(
		`api/form?system_id=${system_id}`,
		"form_dropdown",
		(res) => {},
		false,
	);

	const { data: dataSource, refetch: refetchSource } = GET(
		`api/profile_evaluation?${new URLSearchParams(tableFilter)}`,
		"profile_evaluation_list",
	);

	const onChangeTable = (pagination, filters, sorter) => {
		setTableFilter({
			...tableFilter,
			sort_field: sorter.columnKey,
			sort_order: sorter.order ? sorter.order.replace("end", "") : null,
		});
	};

	useEffect(() => {
		refetchSource();

		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableFilter]);

	const onFinish = (values) => {
		if (!values.form_id) {
			notification.error({
				message: "Report Filter",
				description: "Please select a form",
			});
			return;
		}

		setTableFilter((ps) => ({
			...ps,
			form_id: values.form_id,
			department_ids: values.department_ids ? values.department_ids : [],
			date_range: values.date_range
				? values.date_range.map((item) =>
						item ? dayjs(item).format("YYYY-MM-DD") : null,
					)
				: [],
			rate_status: values.rate_status ? values.rate_status : "",
		}));
	};

	const handlePrint = async (type) => {
		try {
			let data = {
				...tableFilter,
				print: "PDF",
				ids: selectedRowKeys.join(","),
				user_id: userData().id,
				type: type,
			};

			setToggleModalPreviewPdf({
				open: true,
				url: `api/form_evaluation_print?${new URLSearchParams(data)}`,
			});
		} catch (error) {
			console.error("Error fetching PDF:", error);
		}
	};

	return (
		<Row gutter={[20, 20]} id="tbl_wrapper">
			<Col xs={24} sm={24} md={24} lg={24} xl={24}>
				<Form form={form} onFinish={onFinish}>
					<Row gutter={[20, 20]}>
						<Col xs={24} sm={12} md={6} lg={6} xl={6}>
							<Form.Item name="form_id" noStyle>
								<Select
									className="w-100"
									size="large"
									placeholder="Select Form"
									options={
										dataForm && dataForm.data
											? dataForm.data?.map((item) => ({
													label:
														"(" +
														item.school_year +
														") " +
														"(" +
														item.semester +
														") " +
														item.form_name,
													value: item.id,
												}))
											: []
									}
									allowClear
									showSearch
									filterOption={(input, option) => {
										// console.log("option", option);
										return (
											option.label.toLowerCase().indexOf(input.toLowerCase()) >=
											0
										);
									}}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={6} lg={6} xl={6}>
							<Form.Item name="department_ids" noStyle>
								<Select
									className="w-100"
									size="large"
									placeholder="Select Department"
									mode="multiple"
									options={
										dataDepartment && dataDepartment.data
											? dataDepartment.data?.map((item) => ({
													label: item.department_name,
													value: item.id,
												}))
											: []
									}
									allowClear
									showSearch
									filterOption={(input, option) => {
										// console.log("option", option);
										return (
											option.label.toLowerCase().indexOf(input.toLowerCase()) >=
											0
										);
									}}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={6} lg={6} xl={6}>
							<Form.Item name="date_range" noStyle>
								<DatePicker.RangePicker
									className="w-100"
									size="large"
									allowClear
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={6} lg={6} xl={6}>
							<Form.Item name="rate_status" noStyle>
								<Select
									className="w-100"
									size="large"
									placeholder="Rate Status"
									options={[
										{ label: "All", value: "All" },
										{ label: "Rated", value: "Rated" },
										{ label: "No Rating", value: "No Rating" },
									]}
									allowClear
									showSearch
									filterOption={(input, option) => {
										// console.log("option", option);
										return (
											option.label.toLowerCase().indexOf(input.toLowerCase()) >=
											0
										);
									}}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={24} md={24} lg={24} xl={24}>
							<Flex gap={10}>
								<Button
									className="btn-main-primary"
									size="large"
									icon={<FontAwesomeIcon icon={faFilter} />}
									htmlType="submit"
								>
									Filter
								</Button>
								{tableFilter.form_id && (
									<>
										<Button
											size="large"
											icon={<FontAwesomeIcon icon={faFilePdf} />}
											onClick={() => {
												handlePrint("DETAILED_EVALUATION");
											}}
										>
											PDF Evaluation Export
										</Button>
										<Button
											size="large"
											icon={<FontAwesomeIcon icon={faFilePdf} />}
											onClick={() => {
												handlePrint("SUBJECT_EVALUATION");
											}}
										>
											PDF Subject Export
										</Button>
									</>
								)}
							</Flex>
						</Col>
					</Row>
				</Form>
			</Col>
			<Col xs={24} sm={24} md={24} lg={24} xl={24}>
				<Flex align="center" justify="space-between">
					<TableGlobalSearchAnimated
						tableFilter={tableFilter}
						setTableFilter={setTableFilter}
					/>

					<div>
						<span className="font-700">Total:</span>{" "}
						{dataSource && dataSource.data ? dataSource.data.length : 0} entries
					</div>
				</Flex>
			</Col>
			<Col xs={24} sm={24} md={24} lg={24} xl={24}>
				<Table
					dataSource={dataSource && dataSource.data ? dataSource.data : []}
					rowKey={(record) => record.id}
					pagination={false}
					bordered={false}
					onChange={onChangeTable}
					scroll={{ x: "max-content" }}
					sticky
					rowSelection={{
						selectedRowKeys,
						onChange: (selectedRowKeys) => {
							setSelectedRowKeys(selectedRowKeys);
						},
					}}
				>
					<Table.Column
						title="Faculty ID"
						key="school_id"
						dataIndex="school_id"
						sorter={true}
						width={200}
					/>
					<Table.Column
						title="Full Name"
						key="fullname"
						dataIndex="fullname"
						sorter={true}
						width={250}
					/>
					<Table.Column
						title="Departments"
						key="departments"
						render={(_, record) => {
							let faculty_loads = record.faculty_loads.reduce((acc, curr) => {
								if (
									!acc.some(
										(item) => item.ref_department.id === curr.ref_department.id,
									)
								) {
									acc.push(curr);
								}
								return acc;
							}, []);

							return faculty_loads.map((item) => (
								<Tag key={item.id}>{item.ref_department.department_name}</Tag>
							));
						}}
					/>
					<Table.Column
						title="Rate Status"
						key="form_question_answers_count"
						dataIndex="form_question_answers_count"
						align="center"
						render={(text, _) => (text > 0 ? "Rated" : "Not Rated")}
					/>
				</Table>
			</Col>

			<ModalPreviewPdf
				toggleModalPreviewPdf={toggleModalPreviewPdf}
				setToggleModalPreviewPdf={setToggleModalPreviewPdf}
			/>
		</Row>
	);
}
